import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarClock, ChevronDown, ChevronLeft, ChevronRight, FileUp, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EDIT_ROLES = ["owner", "superadmin", "admin"];

/**
 * Days Missed report (Reports tab): every employee — active or not — with
 * booked-off, sick and no-show day counts for a chosen year, expandable to a
 * month-by-month grid. Yearly totals sit at the top. Owners/admins can edit
 * any value inline (an edit overrides the computed number for that year;
 * no-shows are manual-only) and superadmins/admins can import pre-app
 * records from any file (AI-scanned) and undo an import.
 */
export default function DaysMissedReport() {
  const { user } = useAuth();
  const role = user?.role || "";
  const canImport = role === "superadmin" || role === "admin";
  const canEdit = EDIT_ROLES.includes(role);

  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [imports, setImports] = useState([]);
  const [importing, setImporting] = useState(false);
  const [busyImport, setBusyImport] = useState("");
  const [editing, setEditing] = useState(null); // `${empId}:${column}`
  const [draft, setDraft] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async (y) => {
    try {
      const r = await api.get("/reports/days-missed", { params: { year: y } });
      setData(r.data);
    } catch {
      toast.error("Could not load the days-missed report");
    }
  }, []);
  useEffect(() => { load(year); }, [year, load]);

  const loadImports = useCallback(async () => {
    if (!canImport) return;
    try {
      const r = await api.get("/reports/days-missed/imports");
      setImports(r.data || []);
    } catch { /* non-fatal */ }
  }, [canImport]);
  useEffect(() => { loadImports(); }, [loadImports]);

  const saveManual = async (empId, column, current, month = null) => {
    const trimmed = draft.trim();
    setEditing(null);
    if (trimmed === String(current)) return; // unchanged
    const value = trimmed === "" ? null : parseInt(trimmed, 10);
    if (value !== null && (Number.isNaN(value) || value < 0 || value > 366)) {
      toast.error("Enter a number of days (0–366), or clear it to use the computed value");
      return;
    }
    try {
      const body = { employee_id: empId, year, column, value };
      if (month != null) body.month = month;
      await api.put("/reports/days-missed/manual", body);
      await load(year);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save that value");
    }
  };

  const doImport = async (f) => {
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/reports/days-missed/import", fd);
      const { matched = [], unmatched = [] } = r.data || {};
      const matchedNames = [...new Set(matched.map((m) => m.matched_to || m.name))];
      const unmatchedNames = [...new Set(unmatched.map((u) => u.name))];
      if (matchedNames.length) toast.success(`Imported absence records for ${matchedNames.length} employee${matchedNames.length === 1 ? "" : "s"}: ${matchedNames.slice(0, 4).join(", ")}${matchedNames.length > 4 ? "…" : ""}`);
      if (unmatchedNames.length) {
        toast.warning(
          `Couldn't match these names to employees: ${unmatchedNames.slice(0, 4).join(", ")}${unmatchedNames.length > 4 ? "…" : ""}`,
          { duration: 8000 },
        );
      }
      await load(year);
      await loadImports();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteImport = async (imp) => {
    if (!window.confirm(`Undo the import "${imp.filename}" (${imp.rows} record${imp.rows === 1 ? "" : "s"})?\n\nIts days are stripped from the report immediately. Timesheets and payroll are never touched.`)) return;
    setBusyImport(imp.import_id);
    try {
      await api.delete(`/reports/days-missed/import/${imp.import_id}`);
      toast.success("Import undone — its days are off the report");
      await load(year);
      await loadImports();
    } catch {
      toast.error("Could not remove that import");
    } finally {
      setBusyImport("");
    }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || { booked_off_days: 0, sick_days: 0, no_show_days: 0, total_days: 0 };
  const currentYear = data?.current_year || thisYear;

  const Cell = ({ empId, column, value, manual, color }) => {
    const key = `${empId}:${column}`;
    const isEditing = editing === key;
    const testid = `days-missed-${column.replace("_", "-")}-${empId}`;
    if (isEditing) {
      return (
        <input
          autoFocus
          type="number"
          min="0"
          max="366"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveManual(empId, column, value);
            if (e.key === "Escape") setEditing(null);
          }}
          onBlur={() => saveManual(empId, column, value)}
          data-testid={`${testid}-input`}
          className={`w-11 rounded border border-sky-500/50 bg-white/10 px-1 py-0.5 text-right font-mono text-xs text-white outline-none`}
        />
      );
    }
    const inner = (
      <>
        {value}
        {manual && <span className="ml-0.5 text-[8px] text-emerald-400" title="Manually set">*</span>}
      </>
    );
    if (!canEdit) {
      return <span className={`w-11 text-right font-mono text-xs ${color}`} data-testid={testid}>{inner}</span>;
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setDraft(String(value)); setEditing(key); }}
        data-testid={testid}
        title={canEdit ? "Click to edit (Enter saves, Esc cancels, empty reverts to computed)" : undefined}
        className={`w-11 rounded text-right font-mono text-xs ${color} hover:bg-white/10 hover:ring-1 hover:ring-white/20`}
      >
        {inner}
      </button>
    );
  };

  const MonthNoShow = ({ empId, month, value }) => {
    const key = `${empId}:no_show:${month}`;
    const testid = `days-missed-month-noshow-${empId}-${month}`;
    if (canEdit && editing === key) {
      return (
        <input
          autoFocus
          type="number"
          min="0"
          max="366"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveManual(empId, "no_show", value, month);
            if (e.key === "Escape") setEditing(null);
          }}
          onBlur={() => saveManual(empId, "no_show", value, month)}
          data-testid={`${testid}-input`}
          className="w-9 rounded border border-red-500/50 bg-white/10 px-0.5 text-right font-mono text-[10px] text-white outline-none"
        />
      );
    }
    if (!canEdit) {
      return <span className="text-red-500/90 dark:text-red-300/90" data-testid={testid}>N&nbsp;{value}</span>;
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setDraft(String(value)); setEditing(key); }}
        data-testid={testid}
        title="No-shows this month — click to edit"
        className="rounded px-0.5 text-red-500/90 hover:bg-white/10 hover:ring-1 hover:ring-white/20 dark:text-red-300/90"
      >
        N&nbsp;{value}
      </button>
    );
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="reports-days-missed">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">Days missed</h3>
        <div className="mx-auto flex items-center gap-1">
          <button
            onClick={() => setYear((y) => y - 1)}
            data-testid="days-missed-prev-year"
            title="Previous year"
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[4.5rem] text-center font-mono text-sm font-bold text-white" data-testid="days-missed-year">{year}</span>
          <button
            onClick={() => setYear((y) => y + 1)}
            disabled={year >= currentYear}
            data-testid="days-missed-next-year"
            title="Next year"
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white disabled:opacity-30"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          {year !== currentYear && (
            <button
              onClick={() => setYear(currentYear)}
              data-testid="days-missed-this-year"
              className="ml-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-white"
            >
              This year
            </button>
          )}
        </div>
        {canImport && (
          <>
            <input
              ref={fileRef}
              type="file"
              data-testid="days-missed-import-input"
              className="hidden"
              onChange={(e) => doImport(e.target.files?.[0])}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              data-testid="days-missed-import-btn"
              title="Import pre-app records from any file (PDF, image, Excel, CSV, text, Word) — the AI scans it"
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-sky-600 hover:bg-sky-500/20 disabled:opacity-50 dark:text-sky-300"
            >
              {importing ? <span className="animate-pulse">Scanning…</span> : <><FileUp className="h-3 w-3" /> Import</>}
            </button>
          </>
        )}
      </div>
      <p className="mt-1 font-mono text-[10px] text-white/35">
        Sick days, booked-off days and no-shows per employee, from timesheets plus imported pre-app records. Tap a row for the month-by-month split.
        {canEdit && " Tap any number to correct it (empty reverts to the computed value; * = manually set)."}
      </p>

      {/* yearly totals */}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-md border border-white/10 bg-white/[0.03] p-2.5 text-center" data-testid="days-missed-total-all">
          <p className="font-mono text-xl font-bold text-white">{totals.total_days}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Total days missed</p>
        </div>
        <div className="rounded-md border border-white/10 bg-white/[0.03] p-2.5 text-center" data-testid="days-missed-total-booked">
          <p className="font-mono text-xl font-bold text-sky-500 dark:text-sky-300">{totals.booked_off_days}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Booked off</p>
        </div>
        <div className="rounded-md border border-white/10 bg-white/[0.03] p-2.5 text-center" data-testid="days-missed-total-sick">
          <p className="font-mono text-xl font-bold text-amber-600 dark:text-amber-300">{totals.sick_days}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Sick</p>
        </div>
        <div className="rounded-md border border-white/10 bg-white/[0.03] p-2.5 text-center" data-testid="days-missed-total-no-show">
          <p className="font-mono text-xl font-bold text-red-500 dark:text-red-300">{totals.no_show_days}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">No-shows</p>
        </div>
      </div>

      {data === null ? (
        <p className="mt-4 text-center font-mono text-xs text-white/40">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-center font-mono text-xs text-white/40" data-testid="days-missed-empty">No employees found.</p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-md border border-white/10">
          <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-2 border-b border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest text-white/40">
            <span>Employee</span>
            <span className="w-11 text-right">Booked</span>
            <span className="w-11 text-right">Sick</span>
            <span className="w-11 text-right">No show</span>
            <span className="w-11 text-right">Total</span>
          </div>
          {rows.map((r) => (
            <div key={r.employee_id} className="border-b border-white/5 last:border-0" data-testid={`days-missed-row-${r.employee_id}`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setOpen((o) => ({ ...o, [r.employee_id]: !o[r.employee_id] }))}
                onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) setOpen((o) => ({ ...o, [r.employee_id]: !o[r.employee_id] })); }}
                data-testid={`days-missed-toggle-${r.employee_id}`}
                aria-expanded={!!open[r.employee_id]}
                className={`grid w-full cursor-pointer grid-cols-[1fr_auto_auto_auto_auto] items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03] ${r.active ? "" : "opacity-50"}`}
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  {open[r.employee_id]
                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-white/40" />
                    : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/40" />}
                  <span className="min-w-0">
                    <span className="block truncate font-mono text-xs font-semibold text-white">{r.name}</span>
                    <span className="block truncate font-mono text-[9px] text-white/35">
                      {r.trade || "—"}
                      {!r.active && " · inactive"}
                      {r.imported_days > 0 && ` · +${r.imported_days} imported`}
                    </span>
                  </span>
                </span>
                <Cell empId={r.employee_id} column="booked_off" value={r.booked_off_days} manual={r.manual_booked_off} color="text-sky-500 dark:text-sky-300" />
                <Cell empId={r.employee_id} column="sick" value={r.sick_days} manual={r.manual_sick} color="text-amber-600 dark:text-amber-300" />
                <Cell empId={r.employee_id} column="no_show" value={r.no_show_days} manual={r.no_show_days > 0} color="text-red-500 dark:text-red-300" />
                <span className="w-11 text-right font-mono text-xs font-bold text-white" data-testid={`days-missed-total-${r.employee_id}`}>{r.total_days}</span>
              </div>
              {open[r.employee_id] && (
                <div className="grid grid-cols-3 gap-1 border-t border-white/5 bg-black/20 px-3 py-2 sm:grid-cols-6" data-testid={`days-missed-months-${r.employee_id}`}>
                  {r.months.map((m) => {
                    const total = m.booked_off_days + m.sick_days + m.no_show_days;
                    return (
                      <div
                        key={m.month}
                        className={`rounded px-1.5 py-1 text-center ${total > 0 ? "bg-white/[0.06]" : ""}`}
                        data-testid={`days-missed-month-${r.employee_id}-${m.month}`}
                      >
                        <p className={`font-mono text-[9px] uppercase tracking-widest ${total > 0 ? "text-white/60" : "text-white/35"}`}>{MONTHS[m.month - 1]}</p>
                        <p className={`font-mono text-[10px] ${total > 0 || m.no_show_days > 0 ? "text-white/80" : "text-white/35"}`}>
                          <span className="text-sky-500/90 dark:text-sky-300/90">B&nbsp;{m.booked_off_days}</span>
                          <span className="mx-0.5 text-white/20">·</span>
                          <span className="text-amber-600/90 dark:text-amber-300/90">S&nbsp;{m.sick_days}</span>
                          <span className="mx-0.5 text-white/20">·</span>
                          <MonthNoShow empId={r.employee_id} month={m.month} value={m.no_show_days} />
                        </p>
                      </div>
                    );
                  })}
                  {r.no_show_lump > 0 && (
                    <p className="col-span-3 pt-1 font-mono text-[9px] text-red-500/80 sm:col-span-6 dark:text-red-300/80" data-testid={`days-missed-lump-${r.employee_id}`}>
                      + {r.no_show_lump} year-level no-show{r.no_show_lump === 1 ? "" : "s"} (set from the collapsed row — not tied to a month).
                    </p>
                  )}
                  {(r.imported_days > 0 || r.manual_booked_off || r.manual_sick || r.no_show_lump > 0) && (
                    <p className="col-span-3 pt-1 font-mono text-[9px] text-white/35 sm:col-span-6">
                      Months combine timesheet days and dated imported entries — year-level manual values show in the yearly total only.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canImport && imports.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="days-missed-imports">
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/35">Imported pre-app records</p>
          {imports.map((imp) => (
            <div key={imp.import_id} className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-1.5"
              data-testid={`days-missed-import-${imp.import_id}`}>
              <p className="min-w-0 truncate font-mono text-[10px] text-white/60">
                <CalendarClock className="mr-1 inline h-3 w-3 text-white/35" />
                {imp.filename} · {imp.rows} record{imp.rows === 1 ? "" : "s"}
                {imp.years?.length > 0 && ` · ${imp.years.join(", ")}`} · by {imp.imported_by}
              </p>
              <button
                onClick={() => deleteImport(imp)}
                disabled={busyImport === imp.import_id}
                data-testid={`days-missed-import-delete-${imp.import_id}`}
                title="Undo this import — strips its days from the report"
                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-red-500 hover:bg-red-500/20 disabled:opacity-50 dark:text-red-300"
              >
                <Undo2 className="h-3 w-3" /> {busyImport === imp.import_id ? "Undoing…" : "Undo"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
